import React, { ReactElement, useEffect, useState } from 'react';
import classNames from 'classnames/bind';

import { sendConfirmationEmail } from 'api/requests';

import { emailValidator, emptyValidator } from 'utils/validator';
import { emailBlacklistValidator } from 'utils/validator';
import { GOOGLE_KEY } from 'utils/constants';
import { reCaptcha } from 'utils/helpers/reCaptcha';
import service from 'error-handler/functions/service';

import RootLayout from 'layouts/RootLayout';
import Input from 'ui/Input';

import styles from 'pages/auth/forgot-password/ForgotPassword.module.scss';

const cn = classNames.bind(styles);

enum EForm {
  email = 'email',
};

interface IFormData {
  email: string;
};

export default function ForgotPassword() {
  const [formData, setFormData] = useState<IFormData>({ email: '' });
  const [errors, setErrors] = useState<IFormData>({ email: '' });
  const [message, setMessage] = useState<string>('by providing us with your corporate email:');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  useEffect(() => {
    reCaptcha();
  }, []);

  const validator = (): boolean => {
    return (
      emailValidator({ formData, setErrors }) &&
      emailBlacklistValidator({ formData, setErrors }) &&
      emptyValidator({ formData, setErrors, key: 'email', minChar: 3 })
    );
  };

  const setState = (messageValue: string, errorStatus: boolean, successStatus: boolean) => {
    setMessage(messageValue);
    setIsError(errorStatus);
    setIsSuccess(successStatus);
  };

  const submitEmailHandler = async (event: any) => {
    event.preventDefault();

    if (!validator()) return;

    let tokenRes: any;

    // Recaptcha
    await grecaptcha.execute(GOOGLE_KEY, { action: 'submit' }).then((token: any) => (tokenRes = token));

    const newFormData = {
      email: formData.email,
      'g-recaptcha-response': tokenRes,
    };

    setIsLoading(true);

    try {
      const response: any = await sendConfirmationEmail(newFormData);

      if (response.status === 200) {
        setState(response.data.data.message, false, true);
      }
    } catch (error: any) {
      // console.log('>>> FORGOT_PASSWORD -> error?.response?.data?.errors[0].message: ', error?.response?.data?.errors[0].message);
      if (error?.response?.status === 400 || error?.response?.status === 404) {
        setState('Email not found', true, false);
      } else {
        setState('Sorry something went wrong', true, false);
      }
    }
    setIsLoading(false);
  };

  return (
    <div className={cn('forgot-password')}>
      <div className={cn('container')}>
        {isSuccess ? (
          <p className={cn('form__paragraph', 'confirmed')}>
            We have sent you an email with a password restoration link. By clicking on the link you will be redirected
            to the page where you can reset your password.
          </p>
        ) : (
          <form
            className={cn('form')}
            onSubmit={(event: any) => service(event)('submitEmailHandler')(submitEmailHandler)}
          >
            <h1 className={cn('form__heading')}>Restore your password</h1>
            <p className={cn('form__paragraph', { 'is-error': isError })}>
              {message}
            </p>
            <div className={cn('form__section')}>
              <Input
                name={EForm.email}
                placeholder="Your corporate email"
                error={errors.email}
                setFormData={setFormData}
                setErrors={setErrors}
                value={formData.email}
              />
            </div>
            <div className={cn('form__buttons')}>
              <button className={cn('form__button-submit')} type="submit">
                {isLoading ? <div className={cn('form__preloader')} /> : ''}
                Submit
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export const getStaticProps = async () => {
  return {
    props: {
      title: 'Forgot Password',
      path: '/auth/forgot-password',
    },
  };
};

ForgotPassword.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};
